import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDataSelector } from 'store/selectors/user';
import { Popover } from 'components-antd';
import { Wrapper } from 'components';
import { Categories, FormValues } from './components/NeighborhoodTab/components/Filter/types';
import { tabRoutes, TabRoutesLinks } from './constants';
import { NeighborhoodTab, PropertyTab } from './components';
import { PageHeader } from '../../components';
import { Icons, IconVariant } from './components/icons';
import { cloneDeep, map } from 'lodash-es';
import { useLocation } from 'react-router-dom';
import { getCategoryAndStatusFromFilter } from 'pages/Radar';
import { NeighborhoodFilterTypes } from 'pages/Radar/types';
import {
  getNeighborhoodActivityDataSelector,
  getRemindersByIdSelector,
  getValuationDataSelector,
} from 'store/selectors/intel';
import {
  requestGetAddressNeighborhoodActivityEffect,
  requestGetAddressValuationDetailsEffect,
  requestGetRemindersClientEffect,
} from 'store/effects/intel';
import classNames from 'classnames';
import { showErrorMessage } from 'helpers';
import { getAgentTeamIntelTagsForClient } from 'api/intel/agent';
import { getCurrentContextSelector } from 'store/selectors/context';

export const getAddressFormattedCassAddress = (
  cassAddress: any,
): { address1: string; address2: string } => {
  if (!cassAddress) return { address1: 'No address Added', address2: '' };
  const streetNumber = cassAddress.components.primary_number;
  const streetName = cassAddress.components.street_name;
  const streetPostDirection = cassAddress.components.street_postdirection
    ? ` ${cassAddress.components.street_postdirection}`
    : '';
  const streetSuffix = cassAddress.components.street_suffix
    ? ` ${cassAddress.components.street_suffix}`
    : '';
  const city = cassAddress.components.city_name;
  const state = cassAddress.components.state_abbreviation;
  const { zipcode } = cassAddress.components;

  const address1 = `${streetNumber} ${streetName}${streetPostDirection}${streetSuffix}`.trim();

  const address2 = `${city}, ${state} ${zipcode}`;

  return { address1, address2 };
};

export const getCityAndStateFromCassAddress = (
  cassAddress: any,
): { city: string; state: string } => {
  if (!cassAddress) return { city: '', state: '' };

  const city = cassAddress.components.city_name;
  const state = cassAddress.components.state_abbreviation;

  // Return the formatted addresses
  return { city, state };
};

interface IAddressDropdown {
  address1: string;
  address2: string;
  search: string;
  setAddress1: any;
  setAddress2: any;
  setSearch: any;
  setAddressId: any;
  isAddressChanged: any;
}

const AddressDropdown: FC<IAddressDropdown> = ({
  address1,
  address2,
  setAddress1,
  setAddress2,
  search,
  setSearch,
  setAddressId,
  isAddressChanged,
}) => {
  const user = useSelector(getUserDataSelector);
  const [visible, setVisible] = useState(false);
  const addresses = user?.Client?.Address || [];
  const filteredAddresses = search?.length
    ? addresses.filter(({ address }) => {
        const { address1, address2 } = getAddressFormattedCassAddress(address);
        return (address1 + address2).includes(search);
      })
    : addresses;
  return (
    <div className={styles.dropdownWrapper}>
      <Popover
        open={visible}
        onOpenChange={setVisible}
        trigger="click"
        placement="bottom"
        overlayClassName={styles.overlay}
        content={
          <div className={styles.content}>
            {/* <div className={styles.search}>
              <span>
                <Icons variant={IconVariant.GEO_TAG} />
              </span>
              <div className={styles.input}>
                <input
                  placeholder="Enter an address, neighborhood, city, or zip code..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                />
              </div>
              <div className={styles.searchButton}>
                <Icons variant={IconVariant.SEARCH} />
              </div>
            </div>
            <div className={styles.divider} /> */}
            <p className={styles.title}>Properties</p>
            <div>
              {map(filteredAddresses, ({ address, Id }) => {
                const { address1: address1Line, address2: address2Line } =
                  getAddressFormattedCassAddress(address);
                const isSelected = address1Line === address1 && address2Line === address2;
                return (
                  <div
                    className={styles.item}
                    onClick={() => {
                      setAddress1(address1Line);
                      setAddress2(address2Line);
                      setAddressId(Number(Id));
                      isAddressChanged.current = true;
                      setVisible(false);
                    }}
                  >
                    <span>
                      <Icons variant={IconVariant.GEO_TAG_GREY} />
                    </span>
                    <div className={styles.addressText}>
                      <p className={styles.line1}>{address1Line}</p>
                      <p className={styles.line2}>{address2Line}</p>
                    </div>
                    {isSelected && (
                      <span>
                        <Icons variant={IconVariant.TICK} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        }
      >
        <div className={styles.address}>
          {address1} <Icons variant={IconVariant.ARROW_DOWN} />
        </div>
      </Popover>
    </div>
  );
};

export const IntelClient: FC = () => {
  const pageSize = 20;
  const dispatch = useDispatch();
  const location = useLocation();
  const { address1Param, address2Param, addressIdParam } = (location.state || {}) as any;
  const valuationData = useSelector(getValuationDataSelector);
  const remindersData = useSelector(getRemindersByIdSelector);
  const { isPending: neighborhoodPending } = useSelector(getNeighborhoodActivityDataSelector);
  const user = useSelector(getUserDataSelector);
  const addresses = user?.Client?.Address || [];
  const { address1: initialAddress1, address2: initialAddress2 } = getAddressFormattedCassAddress(
    addresses?.[0]?.address,
  );
  const [neighborhoodData, setNeighborhoodData] = useState<any>([]);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(TabRoutesLinks[0].value);
  const [address1, setAddress1] = useState<string>(address1Param || initialAddress1);
  const [address2, setAddress2] = useState<string>(address2Param || initialAddress2);
  const [addressId, setAddressId] = useState<number>(Number(addressIdParam || addresses?.[0]?.Id));
  const [search, setSearch] = useState<string>('');
  const selectedAddress = user?.Client?.Address?.find(({ Id }) => Id === addressId);
  const isAddressChanged = useRef(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [systemTags, setSystemTags] = useState<any[]>([]);
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const context = useSelector(getCurrentContextSelector);

  useEffect(() => {
    fetchTags();
  }, [context]);

  const fetchTags = async () => {
    const currentAgentId = context?.Agents[0]?.Id;

    if (!currentAgentId) return;

    setLoading(true);
    try {
      const response = await getAgentTeamIntelTagsForClient(currentAgentId);
      if (response.status === 200)
        setSystemTags(response.data?.data?.length ? response.data.data : []);
    } catch (e) {
      showErrorMessage('Unable to fetch tags.');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!selectedAddress) return;

    dispatch(
      requestGetAddressValuationDetailsEffect({
        address1,
        address2,
        addressId,
        clientId: user?.Client?.Id,
      }),
    );

    dispatch(
      requestGetRemindersClientEffect({
        Address1: address1,
        Address2: address2,
        UserId: user?.Client?.Id,
        addressId,
      }),
    );
  }, [address1, address2, addressId, user, selectedAddress]);

  useEffect(() => {
    if (!selectedAddress) return;

    const currentAgentId = context?.Agents[0]?.Id;
    if (!currentAgentId || !page) return;

    const { city, state } = getCityAndStateFromCassAddress(selectedAddress?.address);

    dispatch(
      requestGetAddressNeighborhoodActivityEffect(
        true,
        {
          clientId: user?.Client?.Id,
          address: {
            long: selectedAddress?.address?.metadata?.longitude,
            lat: selectedAddress?.address?.metadata?.latitude,
            radius: selectedAddress?.neighbourhoodRadius ?? 1,
          },
          limit: pageSize,
          activityTypes: ['Agent', 'Property'],
          agentIds: [currentAgentId],
          ...(!isAddressChanged.current && cursor ? { cursor } : {}),
          ...getCategoryAndStatusFromFilter(NeighborhoodFilterTypes.All),
          ...(startDate ? { fromTimestamp: startDate } : { fromTimestamp: undefined }),
          ...(endDate ? { toTimestamp: endDate } : { toTimestamp: undefined }),
          ...(city || state
            ? {
                locations: [
                  {
                    ...(city && { City: city }),
                    ...(state && { State: state }),
                  },
                ],
              }
            : {}),
        },
        {},
        (error, payload) => {
          if (!error) {
            const results = payload?.data?.data?.results || [];
            if (results.length)
              setNeighborhoodData(
                isAddressChanged.current ? results : (prev) => [...prev, ...results],
              );
            setCursor(
              payload?.data?.data?.nextCursor ? payload?.data?.data?.nextCursor : undefined,
            );
          } else if (isAddressChanged.current) {
            setNeighborhoodData([]);
          }
          isAddressChanged.current = false;
        },
      ),
    );
  }, [address1, address2, selectedAddress, addressId, page, context]);

  const handleFiltersChange = useCallback(
    (values: FormValues) => {
      if (!selectedAddress) return;

      const currentAgentId = context?.Agents[0]?.Id;
      if (!currentAgentId) return;

      const categories = cloneDeep(values?.categories);
      const startDateNew = values?.startDate;
      const endDateNew = values?.endDate;
      setStartDate(startDateNew);
      setEndDate(endDateNew);
      setPage(0);
      setNeighborhoodData([]);

      let category: string[] | undefined = undefined;

      if (Object.values(categories).every((value) => !value)) {
        Object.keys(categories).forEach((key) => {
          categories[key] = true;
        });
      }

      let fetchAgentIntel = false;

      if (categories[Categories.NEW_LISTING]) {
        category = [...(category ?? []), 'New'];
      }
      if (categories[Categories.PRICE_REDUCTION]) {
        category = [...(category ?? []), 'Price Reductions'];
      }
      if (categories[Categories.PRICE_INCREASE]) {
        category = [...(category ?? []), 'Price Increase'];
      }
      if (categories[Categories.BACK_ON_THE_MARKET]) {
        category = [...(category ?? []), 'Back on Market'];
      }
      if (categories[Categories.WITHDRAWN]) {
        category = [...(category ?? []), Categories.WITHDRAWN];
      }
      if (categories[Categories.UNDER_CONTRACT]) {
        category = [...(category ?? []), Categories.UNDER_CONTRACT];
      }
      if (categories[Categories.EXPIRED]) {
        category = [...(category ?? []), Categories.EXPIRED];
      }
      if (categories[Categories.SOLD]) {
        category = [...(category ?? []), Categories.SOLD];
      }
      if (categories[Categories.AGENT_UPDATES]) {
        fetchAgentIntel = true;
      }

      const { city, state } = getCityAndStateFromCassAddress(selectedAddress?.address);

      const filterTags = values?.tags
        ? Object.keys(values.tags).filter((key) => values.tags[key] === true)
        : [];

      dispatch(
        requestGetAddressNeighborhoodActivityEffect(
          true,
          {
            clientId: user?.Client?.Id,
            category,
            address: {
              long: selectedAddress?.address?.metadata?.longitude,
              lat: selectedAddress?.address?.metadata?.latitude,
              radius: selectedAddress?.neighbourhoodRadius ?? 1,
            },
            limit: pageSize,
            activityTypes: fetchAgentIntel ? ['Agent', 'Property'] : ['Property'],
            agentIds: [currentAgentId],
            ...(filterTags.length && { tags: filterTags }),
            ...(startDateNew ? { fromTimestamp: startDateNew } : { fromTimestamp: undefined }),
            ...(endDateNew ? { toTimestamp: endDateNew } : { toTimestamp: undefined }),
            ...(city || state
              ? {
                  locations: [
                    {
                      ...(city && { City: city }),
                      ...(state && { State: state }),
                    },
                  ],
                }
              : {}),
          },
          {},
          (error, payload) => {
            if (!error) {
              setCursor(payload?.data?.data?.nextCursor ?? undefined);
              if (payload?.data?.data?.results?.length)
                setNeighborhoodData([...payload.data.data.results]);
            }
          },
        ),
      );
    },
    [dispatch, selectedAddress, user?.Client?.Id, context],
  );

  const propertyTabParams = useMemo(
    () => ({
      address1,
      address2,
      clientId: user?.Client?.Id,
    }),
    [address1, address2, user],
  );

  const renderActiveTab = useMemo(() => {
    switch (activeTab) {
      case tabRoutes.property:
        return (
          <PropertyTab
            params={propertyTabParams}
            states={{
              Address1: address1,
              Address2: address2,
              UserId: user?.Client?.Id,
            }}
          />
        );
      default:
      case tabRoutes.neighborhood:
        return (
          <NeighborhoodTab
            onFilterChange={handleFiltersChange}
            setPage={setPage}
            page={page}
            totalPages={0}
            neighborhoodData={neighborhoodData}
            systemTags={systemTags}
            cursor={cursor}
          />
        );
    }
  }, [activeTab, address1, address2, addressId, neighborhoodData, cursor, systemTags]);

  return (
    <div className={classNames(styles.pageWrapperInternal, styles.pageWrapperInternalClient)}>
      <PageHeader links={TabRoutesLinks} activeKey={activeTab} setActiveTab={setActiveTab}>
        <AddressDropdown
          address1={address1}
          address2={address2}
          setAddress1={setAddress1}
          setAddress2={setAddress2}
          setAddressId={setAddressId}
          search={search}
          setSearch={setSearch}
          isAddressChanged={isAddressChanged}
        />
      </PageHeader>
      <Wrapper
        isPending={
          loading ||
          valuationData.isPending ||
          remindersData.isPending ||
          (isAddressChanged.current && neighborhoodPending)
        }
        className={styles.clientIntelWrapper}
      >
        {renderActiveTab}
      </Wrapper>
    </div>
  );
};
